import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    fetchClasses, fetchClassSections, fetchClassSubjects, createClassSubject,
    type ClassApiRecord, type SectionApiRecord, type ClassSubjectRecord,
} from '../../services/classApi';
import { fetchSchoolSubjects, type SchoolSubjectRecord } from '../../services/subjectApi';
import { getSchoolId } from '../../services/auth';
import { fetchStaffList, type StaffApiRecord } from '../../services/staffApi';
import type { ApiError } from '../../services/api';
import '../subjects/SubjectMaster.css';
import './SubjectAllocation.css';

/** Per-row editable fields for teacher + weekly classes */
type RowSettings = {
    teacherId: string;
    weeklyClasses: number;
};

function getStaffDisplayName(s: StaffApiRecord): string {
    const first = s.user?.first_name ?? '';
    const last = s.user?.last_name ?? '';
    return [first, last].filter(Boolean).join(' ');
}

export default function SubjectAllocation() {
    const [schoolId] = useState(() => getSchoolId());
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sectionsForClass, setSectionsForClass] = useState<SectionApiRecord[]>([]);
    const [sectionsLoading, setSectionsLoading] = useState(false);
    const [teachers, setTeachers] = useState<StaffApiRecord[]>([]);

    // Dropdown (pending) state — not yet committed
    const [pendingClass, setPendingClass] = useState('');
    const [pendingSection, setPendingSection] = useState('');

    // Committed state — drives data load
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedSection, setSelectedSection] = useState('');

    // Display names captured at commit time
    const [committedClassName, setCommittedClassName] = useState('');
    const [committedSectionName, setCommittedSectionName] = useState('');

    /* ---- data ---- */
    const [schoolSubjects, setSchoolSubjects] = useState<SchoolSubjectRecord[]>([]);
    const [allocations, setAllocations] = useState<Map<string, ClassSubjectRecord>>(new Map());
    const [rowSettings, setRowSettings] = useState<Map<string, RowSettings>>(new Map());
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [search, setSearch] = useState('');
    const [togglingId, setTogglingId] = useState<string | null>(null);

    /* ============== Load classes & teachers on mount ============== */
    useEffect(() => {
        fetchClasses({ perPage: 100 })
            .then((res) => setClasses(res.data))
            .catch(() => { /* silent */ });

        fetchStaffList({ isTeacher: true, perPage: 200 })
            .then((res) => setTeachers(res.data))
            .catch(() => { /* silent */ });
    }, []);

    /* ============== Load sections when class changes ============== */
    useEffect(() => {
        if (!pendingClass) return;
        setSectionsLoading(true);
        let cancelled = false;
        fetchClassSections(pendingClass, { perPage: 100 })
            .then((res) => { if (!cancelled) setSectionsForClass(res.data); })
            .catch(() => { if (!cancelled) setSectionsForClass([]); })
            .finally(() => { if (!cancelled) setSectionsLoading(false); });
        return () => { cancelled = true; };
    }, [pendingClass]);

    /* ============== Load school subjects + class allocations ============== */
    const loadData = useCallback(async () => {
        if (!schoolId || !selectedClass) return;
        setLoading(true);
        setErrorMsg('');
        try {
            const [schoolSubj, classSubj] = await Promise.all([
                fetchSchoolSubjects(schoolId, { perPage: 200, isActive: true }),
                fetchClassSubjects(selectedClass, { perPage: 200, sectionId: selectedSection || undefined }),
            ]);
            setSchoolSubjects(schoolSubj);

            const allocMap = new Map<string, ClassSubjectRecord>();
            const settings = new Map<string, RowSettings>();
            for (const cs of classSubj.data) {
                if (cs.school_subject_id) {
                    allocMap.set(cs.school_subject_id, cs);
                    settings.set(cs.school_subject_id, {
                        teacherId: cs.assigned_teacher?.id ?? '',
                        weeklyClasses: cs.weekly_classes_count ?? 5,
                    });
                }
            }
            setAllocations(allocMap);
            setRowSettings((prev) => {
                const merged = new Map(settings);
                // Keep user edits for unallocated rows
                for (const [k, v] of prev) {
                    if (!merged.has(k)) merged.set(k, v);
                }
                return merged;
            });
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to load subjects.');
        } finally {
            setLoading(false);
        }
    }, [schoolId, selectedClass, selectedSection]);

    useEffect(() => {
        if (selectedClass) {
            loadData();
        } else {
            setSchoolSubjects([]);
            setAllocations(new Map());
            setRowSettings(new Map());
        }
    }, [loadData, selectedClass, selectedSection]);

    /* ============== Handle class change ============== */
    const handleClassChange = (id: string) => {
        setPendingClass(id);
        setPendingSection('');
        setSectionsForClass([]);
        setSectionsLoading(!!id);
    };

    /* ============== Get row settings (with defaults) ============== */
    const getRowSettings = (orgSubjectId: string): RowSettings => {
        return rowSettings.get(orgSubjectId) ?? { teacherId: '', weeklyClasses: 5 };
    };

    /* ============== Update row settings ============== */
    const updateRowSetting = (orgSubjectId: string, patch: Partial<RowSettings>) => {
        setRowSettings((prev) => {
            const next = new Map(prev);
            const current = next.get(orgSubjectId) ?? { teacherId: '', weeklyClasses: 5 };
            next.set(orgSubjectId, { ...current, ...patch });
            return next;
        });
    };

    /* ============== Toggle allocation ============== */
    const handleToggle = async (ss: SchoolSubjectRecord) => {
        if (!selectedClass || togglingId) return;
        const enabling = !allocations.has(ss.id);
        const settings = getRowSettings(ss.id);
        setTogglingId(ss.id);
        setErrorMsg('');
        try {
            await createClassSubject(selectedClass, {
                school_id: schoolId!,
                school_subject_id: ss.id,
                section_id: selectedSection || null,
                assigned_teacher_id: settings.teacherId || null,
                weekly_classes_count: settings.weeklyClasses,
            });
            const name = ss.organization_subject?.subject?.name ?? 'Subject';
            setSuccessMsg(`${name} ${enabling ? 'allocated to' : 'removed from'} this section.`);
            setTimeout(() => setSuccessMsg(''), 5000);
            await loadData();
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || `Failed to ${enabling ? 'allocate' : 'remove'} subject.`);
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setTogglingId(null);
        }
    };

    /* ============== Update teacher / weekly classes for an allocated subject ============== */
    const handleFieldUpdate = async (ss: SchoolSubjectRecord, patch: Partial<RowSettings>) => {
        if (!selectedClass || togglingId) return;
        if (!allocations.has(ss.id)) return;

        const settings = { ...getRowSettings(ss.id), ...patch };
        updateRowSetting(ss.id, patch);
        setTogglingId(ss.id);
        setErrorMsg('');
        try {
            await createClassSubject(selectedClass, {
                school_id: schoolId!,
                school_subject_id: ss.id,
                section_id: selectedSection || null,
                assigned_teacher_id: settings.teacherId || null,
                weekly_classes_count: settings.weeklyClasses,
            });
            await loadData();
        } catch (err) {
            const apiErr = err as ApiError;
            setErrorMsg(apiErr.message || 'Failed to update subject.');
            setTimeout(() => setErrorMsg(''), 5000);
        } finally {
            setTogglingId(null);
        }
    };

    /* ============== Filter & sort: allocated first ============== */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const list = q
            ? schoolSubjects.filter((ss) => {
                const subj = ss.organization_subject?.subject;
                return subj?.name.toLowerCase().includes(q) ||
                    subj?.code.toLowerCase().includes(q);
            })
            : schoolSubjects;

        const allocated: SchoolSubjectRecord[] = [];
        const unallocated: SchoolSubjectRecord[] = [];
        for (const ss of list) {
            if (allocations.has(ss.id)) allocated.push(ss);
            else unallocated.push(ss);
        }
        return [...allocated, ...unallocated];
    }, [schoolSubjects, allocations, search]);

    const hasContext = !!selectedClass;

    return (
        <div className="subjects">
            {successMsg && (
                <div className="subjects__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="subjects__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {errorMsg && (
                <div className="subjects__toast subjects__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <span>{errorMsg}</span>
                    <button className="subjects__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="subjects__header">
                <div className="subjects__header-text">
                    <h1 className="subjects__title">Class Subject Allocation</h1>
                    <p className="subjects__subtitle">Enable or disable subjects for a class section. Allocated subjects will be available for timetable and marks entry.</p>
                </div>
                {hasContext && (
                    <div className="subjects__header-actions">
                        <div className="subjects__search-wrap">
                            <svg className="subjects__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input className="subjects__search" type="text" placeholder="Search subjects…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                )}
            </div>

            {/* Context bar */}
            <div className="alloc__context">
                <span className="alloc__context-label">Class</span>
                <select className="alloc__context-select" value={pendingClass} onChange={(e) => handleClassChange(e.target.value)}>
                    <option value="">Select Class</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>

                <div className="alloc__context-divider" />

                <span className="alloc__context-label">Section</span>
                <select className="alloc__context-select" value={pendingSection} onChange={(e) => setPendingSection(e.target.value)} disabled={!pendingClass || sectionsLoading}>
                    <option value="">{sectionsLoading ? 'Loading…' : 'All Sections'}</option>
                    {sectionsForClass.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>

                <button
                    className="alloc__search-btn"
                    disabled={!pendingClass || loading}
                    onClick={() => {
                        setSelectedClass(pendingClass);
                        setSelectedSection(pendingSection);
                        setCommittedClassName(classes.find((c) => c.id === pendingClass)?.name ?? '');
                        setCommittedSectionName(sectionsForClass.find((s) => s.id === pendingSection)?.name ?? '');
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    Search
                </button>

                {hasContext && (
                    <>
                        <div className="alloc__context-divider" />
                        <span className="alloc__context-info">{committedClassName}{committedSectionName ? ` — Section ${committedSectionName}` : ''} · {allocations.size} allocated</span>
                    </>
                )}
            </div>

            {/* Content */}
            <div className="subjects__table-wrap">
                {!hasContext ? (
                    <div className="subjects__empty">
                        <div className="subjects__empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                        </div>
                        <h3 className="subjects__empty-title">Select a Class</h3>
                        <p className="subjects__empty-text">Choose a class (and optionally a section), then click Search.</p>
                    </div>
                ) : loading ? (
                    <div className="subjects__loading"><div className="subjects__loader"><span className="subjects__loader-dot" /><span className="subjects__loader-dot" /><span className="subjects__loader-dot" /></div></div>
                ) : filtered.length === 0 ? (
                    <div className="subjects__empty">
                        <div className="subjects__empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                        </div>
                        <h3 className="subjects__empty-title">{search ? 'No subjects match' : 'No organization subjects'}</h3>
                        <p className="subjects__empty-text">{search ? 'Try adjusting your search.' : 'Add subjects in the Subject Master List and enable them for this school first.'}</p>
                    </div>
                ) : (
                    <table className="subjects__table">
                        <thead>
                            <tr>
                                <th className="subjects__th">Subject Name</th>
                                <th className="subjects__th">Code</th>
                                <th className="subjects__th">Type</th>
                                <th className="subjects__th">Teacher</th>
                                <th className="subjects__th">Weekly Classes</th>
                                <th className="subjects__th">Allocated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((ss) => {
                                const subj = ss.organization_subject?.subject;
                                const isAllocated = allocations.has(ss.id);
                                const settings = getRowSettings(ss.id);
                                const isBusy = togglingId === ss.id;

                                return (
                                    <tr className="subjects__table-row" key={ss.id}>
                                        <td className="subjects__td subjects__td--name">{subj?.name ?? '—'}</td>
                                        <td className="subjects__td subjects__td--code">{subj?.code ?? '—'}</td>
                                        <td className="subjects__td">
                                            <span className={`subjects__badge ${subj?.type === 'core' ? 'subjects__badge--core' : 'subjects__badge--elective'}`}>
                                                {subj?.type ? subj.type.charAt(0).toUpperCase() + subj.type.slice(1) : '—'}
                                            </span>
                                        </td>
                                        <td className="subjects__td">
                                            <select
                                                className="alloc__context-select alloc__inline-select"
                                                value={settings.teacherId}
                                                disabled={!isAllocated || isBusy}
                                                onChange={(e) => handleFieldUpdate(ss, { teacherId: e.target.value })}
                                            >
                                                <option value="">— None —</option>
                                                {teachers.map((t) => (
                                                    <option key={t.user?.id ?? t.id} value={t.user?.id ?? t.id}>{getStaffDisplayName(t)}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="subjects__td">
                                            <input
                                                type="number"
                                                className="alloc__inline-input"
                                                min={1}
                                                max={20}
                                                value={settings.weeklyClasses}
                                                disabled={!isAllocated || isBusy}
                                                onChange={(e) => updateRowSetting(ss.id, { weeklyClasses: Number(e.target.value) || 5 })}
                                                onBlur={() => {
                                                    if (isAllocated) handleFieldUpdate(ss, { weeklyClasses: settings.weeklyClasses });
                                                }}
                                            />
                                        </td>
                                        <td className="subjects__td">
                                            <div className="subjects__toggle-cell">
                                                {isBusy ? (
                                                    <svg className="subjects__spinner subjects__spinner--inline" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                                ) : (
                                                    <label className="subjects__toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllocated}
                                                            onChange={() => handleToggle(ss)}
                                                            disabled={togglingId !== null}
                                                        />
                                                        <span className="subjects__toggle-track" />
                                                    </label>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
